import {
  FEATURE_NOT_AVAILABLE_CODE,
  isInternalServerErrorMessage,
  isInternalServerErrorResponse,
  NOT_FOUND_CODE,
  USER_FACING_FEATURE_NOT_AVAILABLE_MESSAGE,
  USER_FACING_FEATURE_NOT_AVAILABLE_TITLE,
  USER_FACING_INTERNAL_ERROR_MESSAGE,
  USER_FACING_INTERNAL_ERROR_TITLE,
  USER_FACING_NOT_FOUND_MESSAGE,
  USER_FACING_NOT_FOUND_TITLE,
  UserFriendlyError,
} from '@affine/error';
import {
  CopyIcon,
  InformationFillDuotoneIcon,
  SingleSelectCheckSolidIcon,
} from '@blocksuite/icons/rc';
import type { FC } from 'react';
import { createElement } from 'react';
import { type ExternalToast, toast } from 'sonner';

import { DesktopNotificationCard } from './desktop/notification-card';
import { DesktopNotificationCenter } from './desktop/notification-center';
import { NotificationInfoIcon } from './icons';
import { MobileNotificationCard } from './mobile/notification-card';
import { MobileNotificationCenter } from './mobile/notification-center';
import type { Notification, NotificationCustomRendererProps } from './types';

const NotificationCard = BUILD_CONFIG.isMobileEdition
  ? MobileNotificationCard
  : DesktopNotificationCard;

const NotificationCenter = BUILD_CONFIG.isMobileEdition
  ? MobileNotificationCenter
  : DesktopNotificationCenter;

export { NotificationCenter };

function getNotificationText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

function hasRenderableNotificationText(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

function humanizeErrorCode(value: string): string {
  return value
    .split(/[_.\-\s]+/g)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function getFallbackErrorTitle(notification: Notification): string {
  const error = notification.error;
  const message = getNotificationText(error?.message).trim();
  if (message) return message;

  const code =
    getNotificationText(error?.name).trim() ||
    getNotificationText(error?.code).trim() ||
    getNotificationText(error?.type).trim();
  if (code) return humanizeErrorCode(code);

  return 'Something went wrong';
}

function isInternalServerErrorNotification(
  notification: Notification
): boolean {
  return (
    isInternalServerErrorResponse(notification.error) ||
    isTechnicalInternalServerErrorText(notification.title) ||
    isTechnicalInternalServerErrorText(notification.message)
  );
}

function isTechnicalInternalServerErrorText(value: unknown): boolean {
  const text = getNotificationText(value).trim();
  return text === 'INTERNAL_SERVER_ERROR' || isInternalServerErrorMessage(text);
}

function getInternalServerErrorNotificationTitle(
  notification: Notification
): Notification['title'] {
  if (
    hasRenderableNotificationText(notification.title) &&
    !isTechnicalInternalServerErrorText(notification.title)
  ) {
    return notification.title;
  }

  return USER_FACING_INTERNAL_ERROR_TITLE;
}

function getInternalServerErrorNotificationMessage(
  notification: Notification
): Notification['message'] {
  if (
    hasRenderableNotificationText(notification.message) &&
    !isTechnicalInternalServerErrorText(notification.message)
  ) {
    return notification.message;
  }

  return USER_FACING_INTERNAL_ERROR_MESSAGE;
}

// Machine error codes (e.g. FEATURE_NOT_AVAILABLE) are never user-facing text.
const MACHINE_ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;

function isMachineErrorCodeText(value: unknown): boolean {
  return MACHINE_ERROR_CODE_PATTERN.test(getNotificationText(value).trim());
}

function isFeatureNotAvailableNotification(
  notification: Notification
): boolean {
  return (
    notification.error?.code === FEATURE_NOT_AVAILABLE_CODE ||
    getNotificationText(notification.title).trim() ===
      FEATURE_NOT_AVAILABLE_CODE
  );
}

function isNotFoundNotification(notification: Notification): boolean {
  return (
    notification.error?.code === NOT_FOUND_CODE ||
    getNotificationText(notification.title).trim() === NOT_FOUND_CODE
  );
}

export function normalizeErrorNotification(
  notification: Notification
): Notification {
  if (isFeatureNotAvailableNotification(notification)) {
    return {
      ...notification,
      title: USER_FACING_FEATURE_NOT_AVAILABLE_TITLE,
      message: USER_FACING_FEATURE_NOT_AVAILABLE_MESSAGE,
    };
  }

  if (isNotFoundNotification(notification)) {
    return {
      ...notification,
      title: USER_FACING_NOT_FOUND_TITLE,
      message: USER_FACING_NOT_FOUND_MESSAGE,
    };
  }

  if (isMachineErrorCodeText(notification.title)) {
    return normalizeErrorNotification({
      ...notification,
      title: USER_FACING_INTERNAL_ERROR_TITLE,
    });
  }

  if (isInternalServerErrorNotification(notification)) {
    return {
      ...notification,
      title: getInternalServerErrorNotificationTitle(notification),
      message: getInternalServerErrorNotificationMessage(notification),
    };
  }

  if (
    hasRenderableNotificationText(notification.title) ||
    hasRenderableNotificationText(notification.message)
  ) {
    return notification;
  }

  return {
    ...notification,
    title: getFallbackErrorTitle(notification),
  };
}

export function isConnectivityNotification(
  notification: Notification | UserFriendlyError
): boolean {
  const error =
    notification instanceof UserFriendlyError
      ? notification
      : notification.error;

  if (error) {
    if (error.is?.('NETWORK_ERROR') || error.isStatus?.(504)) return true;

    const code = String(error.code ?? error.name ?? error.type ?? '');
    if (code === 'NETWORK_ERROR') return true;

    const message = String(error.message ?? '');
    if (
      /gateway timeout/i.test(message) ||
      /network error/i.test(message) ||
      /failed to fetch/i.test(message)
    ) {
      return true;
    }
  }

  if (notification instanceof UserFriendlyError) return false;

  const title = getNotificationText(notification.title);
  const message = getNotificationText(notification.message);
  return (
    /gateway timeout/i.test(title) ||
    /gateway timeout/i.test(message) ||
    /^network error$/i.test(title) ||
    /^network error$/i.test(message)
  );
}

/**
 *
 * @returns {string} toastId
 */
export function notify(notification: Notification, options?: ExternalToast) {
  return toast.custom(id => {
    const onDismiss = () => {
      notification.onDismiss?.();
      toast.dismiss(id);
    };
    return <NotificationCard notification={{ ...notification, onDismiss }} />;
  }, options);
}

/**
 * Build a clipboard-friendly error summary from notification data.
 * Used by notify.error() to auto-add a "Copy" action.
 */
export function buildErrorText(notification: Notification): string {
  const parts: string[] = [];
  const hasInternalServerError =
    isInternalServerErrorNotification(notification);

  if (notification.title) {
    parts.push(
      `Error: ${typeof notification.title === 'string' ? notification.title : 'Error'}`
    );
  }

  if (notification.message) {
    parts.push(
      typeof notification.message === 'string'
        ? notification.message
        : String(notification.message)
    );
  }

  const err = notification.error;
  if (err && !hasInternalServerError) {
    if (err.name) parts.push(`Name: ${err.name}`);
    if (err.code) parts.push(`Code: ${err.code}`);
    if (err.message && !parts.includes(err.message)) {
      parts.push(`Message: ${err.message}`);
    }
    if (err.data) {
      try {
        parts.push(`Data: ${JSON.stringify(err.data, null, 2)}`);
      } catch {
        // non-serializable data — skip
      }
    }
    if (err.stacktrace) parts.push(`Stack: ${err.stacktrace}`);
  }

  parts.push(`Time: ${new Date().toISOString()}`);
  return parts.join('\n');
}

function buildCopyErrorAction(
  notification: Notification
): NonNullable<Notification['actions']>[number] {
  return {
    key: 'copy-error',
    label: createElement(CopyIcon, { width: 16, height: 16 }),
    autoClose: false,
    onClick: async () => {
      await navigator.clipboard.writeText(buildErrorText(notification));
      notify.success(
        {
          title: 'Fehlerdetails kopiert',
          message: 'Die Fehlermeldung wurde in die Zwischenablage kopiert.',
        },
        { duration: 2500 }
      );
    },
    buttonProps: {
      style: { minWidth: 'unset', padding: '2px 4px' },
      title: 'Copy error details',
    },
  };
}

notify.error = (
  notification: Notification | UserFriendlyError,
  options?: ExternalToast
) => {
  if (isConnectivityNotification(notification)) {
    return undefined;
  }

  if (notification instanceof UserFriendlyError) {
    notification = {
      error: notification,
    };
  }
  notification = normalizeErrorNotification(notification);

  // Auto-add copy action for all error notifications
  const copyAction = buildCopyErrorAction(notification);
  const actions = notification.actions
    ? [...notification.actions, copyAction]
    : [copyAction];

  return notify(
    {
      icon: <InformationFillDuotoneIcon />,
      style: 'normal',
      theme: 'error',
      ...notification,
      actions,
    },
    options
  );
};

notify.success = (notification: Notification, options?: ExternalToast) => {
  return notify(
    {
      icon: <SingleSelectCheckSolidIcon />,
      style: 'normal',
      theme: 'success',
      ...notification,
    },
    options
  );
};

notify.info = (notification: Notification, options?: ExternalToast) => {
  return notify(
    {
      icon: <NotificationInfoIcon />,
      style: 'normal',
      theme: 'info',
      ...notification,
    },
    options
  );
};

notify.warning = (notification: Notification, options?: ExternalToast) => {
  return notify(
    {
      icon: <NotificationInfoIcon />,
      style: 'normal',
      theme: 'warning',
      ...notification,
    },
    options
  );
};

notify.custom = (
  Component: FC<NotificationCustomRendererProps>,
  options?: ExternalToast
) => {
  return toast.custom(id => {
    return <Component onDismiss={() => toast.dismiss(id)} />;
  }, options);
};

notify.dismiss = toast.dismiss;
